import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { App } from "./App.tsx";
import { Crash } from "./Crash.tsx";
import { store } from "./store.ts";
import "./styles.css";
import { update } from "./update.ts";

/**
 * A new version, as in EinkaufsZettel: a service worker waiting, or an API answer naming another
 * build than this one (Safari kept the old worker for good, signing in again did not help; Martin,
 * 01.11.2026). Either way the app offers "Neu laden" and never reloads by itself.
 */
let registration: ServiceWorkerRegistration | undefined;
let waiting = false;
let applying = false;
/** Exactly one reload; once it has begun, nothing may pull the service worker away. */
let reloading = false;
const reload = () => {
  if (reloading) return;
  reloading = true;
  location.reload();
};
const offer = () => {
  if (update.ready) return;
  update.ready = true;
  window.dispatchEvent(new Event("speisekarte:update"));
};
const updateSW = registerSW({
  onNeedRefresh() {
    waiting = true;
    offer();
  },
  // The new worker took over: our reload, once.
  onNeedReload: reload,
  // As in KellerLog: an installed app on the iPhone comes back from the background without loading,
  // often for days; so look for a new version whenever it comes to the front, and every half hour.
  onRegisteredSW(_url, found) {
    if (!found) return;
    registration = found;
    const check = () => {
      if (navigator.onLine && !update.ready) found.update().catch(() => undefined);
    };
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && check());
    setInterval(check, 30 * 60_000);
  },
});
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
/** Whether the server answers now: without it, a page loaded without the service worker stays white. */
async function reachable() {
  try {
    return (await fetch("/api/health", { cache: "no-store", signal: AbortSignal.timeout(5000) })).ok;
  } catch {
    return false;
  }
}
/** Without the service worker: signs it off, empties its caches and loads the page from the network. */
async function startOver() {
  if (reloading) return;
  reloading = true;
  try {
    const all = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(all.map((found) => found.unregister()));
    await Promise.all((await caches.keys()).map((key) => caches.delete(key)));
  } catch {
    /* reload anyway */
  }
  location.reload();
}
/**
 * "Neu laden": the new service worker takes over and the page reloads. When none is waiting in time,
 * or it does not take over, the page starts over without the service worker, but only when the
 * server answers.
 */
async function apply(): Promise<boolean> {
  if (reloading) return true;
  if (applying) return false;
  applying = true;
  if (!waiting && registration) {
    await registration.update().catch(() => undefined);
    for (let i = 0; i < 25 && !waiting; i += 1) await sleep(200);
  }
  if (waiting) {
    const next = registration?.waiting;
    void updateSW(true);
    for (let i = 0; i < 50 && !reloading; i += 1) {
      await sleep(100);
      if (next?.state === "activated") reload();
    }
    if (reloading) return true;
  }
  if (await reachable()) {
    void startOver();
    return true;
  }
  applying = false;
  return false;
}
update.apply = apply;
update.server = (version) => {
  if (version === __APP_VERSION__ || update.ready) return;
  offer();
  void registration?.update().catch(() => undefined);
};

createRoot(document.getElementById("app")!).render(
  <Crash>
    <App />
  </Crash>,
);
void store.init();
